import { downloadFile } from "@/helpers/file-parse";

export interface DownloadExcelOptions {
  optionsSheets?: { sheetName: string; values: readonly string[] }[];
}

export async function parseExcel(binaryString: string | ArrayBuffer | null): Promise<unknown[]> {
  if (binaryString === null) {
    throw new Error("No excel file provided");
  }

  const {
    read,
    utils: { sheet_to_json },
  } = await import("xlsx");

  const wb = read(binaryString, { type: "binary" });
  const wsname = wb.SheetNames[0];
  const ws = wb.Sheets[wsname];
  const rows = sheet_to_json(ws);
  if (typeof rows[0] !== "object") {
    throw new Error("Invalid data format, or empty sheet");
  }

  // The UI just can't cope otherwise, even on fast machines
  return rows;
}

export async function downloadCsvAsExcel(fileName: string, value: string): Promise<void> {
  fileName = cleanFileName(fileName);

  const { read, write } = await import("xlsx");

  const workbook = read(value, { type: "string" });
  const workbookBinary = write(workbook, { bookType: "xlsx", type: "binary" });

  const blob = new Blob([stringToBinary(workbookBinary)], { type: "application/octet-stream" });
  downloadFile(blob, `${fileName}.xlsx`);
}

export async function downloadExcel(
  fileName: string,
  data: string[][],
  options: DownloadExcelOptions = {},
): Promise<void> {
  const blob = await createExcelBlob(fileName, data, options);
  downloadFile(blob, `${cleanFileName(fileName)}.xlsx`);
}

export async function createExcelBlob(
  fileName: string,
  data: string[][],
  options: DownloadExcelOptions = {},
): Promise<Blob> {
  fileName = cleanFileName(fileName);

  const {
    write,
    utils: { book_new, json_to_sheet, aoa_to_sheet, book_append_sheet },
  } = await import("xlsx");

  const workbook = book_new();
  workbook.Props = { Title: fileName, Author: "Area of People", CreatedDate: new Date() };

  book_append_sheet(workbook, json_to_sheet(data, { skipHeader: true }), "data");

  for (const { sheetName, values } of options.optionsSheets ?? []) {
    book_append_sheet(workbook, aoa_to_sheet(values.map((v) => [v])), sheetName);
  }

  return new Blob([stringToBinary(write(workbook, { bookType: "xlsx", type: "binary" }))], {
    type: "application/octet-stream",
  });
}

export async function convertToCsv(data: any[], opts: { skipHeader?: boolean } = {}): Promise<string> {
  const {
    utils: { book_new, json_to_sheet, sheet_to_csv },
  } = await import("xlsx");

  const workbook = book_new();
  workbook.SheetNames.push("data");
  workbook.Sheets["data"] = json_to_sheet(data, opts);

  return sheet_to_csv(workbook.Sheets["data"]);
}

function stringToBinary(value: string) {
  const buffer = new ArrayBuffer(value.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < value.length; i++) {
    view[i] = value.charCodeAt(i) & 0xff;
  }

  return buffer;
}

function cleanFileName(sheetName: string) {
  return sheetName.replace(/[^a-zA-Z0-9()| _-]+/g, "");
}
